import { update } from 'ramda';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  TournamentBackend,
  tournamentBackend as requestTournamentBackend,
} from '@/api/tournaments';
import { SectionTitle } from '@/components/SectionTitle';
import { Table } from '@/components/Table';
import { amountWithCurrencySymbol, getCurrencyName } from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import { getPlayerTournamentPoints } from '@/helpers/tournaments';
import { useCommonStore } from '@/store/commonStore';

import { ScTextRegular, Spinner } from '@/styles/common';

const TABLE_PER_CHUCK = 25;

interface ScoreTableProps {
  backendTournamentId: string;
  mode?: ThemeMode;
}

export const ScoreTable: FC<ScoreTableProps> = ({
  backendTournamentId,
  mode = ThemeMode.Dark,
}) => {
  const { blackColor, whiteColor, mainColor } = useTheme();
  const [tournamentB, setTournamentB] = useState<TournamentBackend>();
  const [pendingTournamentB, setPendingTournamentB] = useState(false);
  const { t } = useTranslation('common');

  const userTournamentsStatuses = useCommonStore.use.userTournamentsStatuses();

  const getTournamentB = useCallback(async () => {
    try {
      setPendingTournamentB(true);
      const { data: tournamentB } =
        await requestTournamentBackend(backendTournamentId);
      setTournamentB(tournamentB);
    } catch (error) {
      console.log(error);
    } finally {
      setPendingTournamentB(false);
    }
  }, [backendTournamentId]);

  useEffect(() => {
    if (backendTournamentId == null) return;

    getTournamentB();
  }, [backendTournamentId, getTournamentB]);

  const userAwardPlace = useMemo(() => {
    const activeTournamentStatus = userTournamentsStatuses?.find(
      ({ tournamentId }) => tournamentId === Number(backendTournamentId)
    );

    return Number(activeTournamentStatus?.awardPlace);
  }, [userTournamentsStatuses, backendTournamentId]);

  const topPlayers = useMemo(() => {
    if (tournamentB == null) return;

    const { topPlayers, prizes, currency, strategy } = tournamentB;

    let playersList = [...topPlayers].sort(
      (a, b) => a.awardPlace - b.awardPlace
    );

    const shouldReplaceUser =
      userAwardPlace > 6 && playersList[userAwardPlace - 1];

    if (shouldReplaceUser) {
      playersList = update(5, playersList[userAwardPlace - 1], playersList);
    }

    return playersList.slice(0, shouldReplaceUser ? 6 : 5).map((player) => {
      const currentPrize = prizes.find(
        (prize) => prize?.awardPlace === player?.awardPlace
      );

      const userPoints = Math.round(
        getPlayerTournamentPoints(strategy, player)
      );

      const userPlace = player.awardPlace || 'NQ';

      if (currentPrize == null) {
        return {
          '#': userPlace,
          player: player.nickname,
          points: userPoints,
          prize: undefined,
        };
      }

      const totalAward = (({ moneyAward, freespinsCount }) => {
        if (freespinsCount === 0) {
          const roundedMoneyAward = Math.round(Number(moneyAward));

          return amountWithCurrencySymbol(
            getCurrencyName(currency),
            roundedMoneyAward
          );
        }

        return `${freespinsCount} FS`;
      })(currentPrize);

      return {
        '#': userPlace,
        player: player.nickname,
        points: userPoints,
        prize: totalAward,
      };
    });
  }, [tournamentB, userAwardPlace]);

  const activeRowIndex = useMemo(
    () => userAwardPlace && (userAwardPlace > 6 ? 5 : userAwardPlace - 1),
    [userAwardPlace]
  );

  return (
    <>
      <SectionTitle>{t('tournaments.leaderboard')}</SectionTitle>
      {pendingTournamentB ? (
        <Spinner
          size={30}
          style={{ margin: '30px auto' }}
          color={mainColor}
          background={mode === ThemeMode.Light ? whiteColor : blackColor}
        />
      ) : topPlayers ? (
        <Table
          data={topPlayers}
          headersTransPath="common:tournaments"
          perChunk={TABLE_PER_CHUCK}
          columnWidth={[25, 25, 25, 25]}
          mode={mode}
          activeRowIndex={activeRowIndex}
        />
      ) : (
        <ScTextRegular>{t('displaysInTheGames')}</ScTextRegular>
      )}
    </>
  );
};
